import { parseAnsi, styleFor } from '../core/ansi.js';
import {
  bold,
  brightCyan,
  brightGreen,
  brightRed,
  brightYellow,
  cyan,
  dim,
  green,
} from '../core/color.js';
import type { PluginInstall } from '../core/kernel.js';
import type { Ctx } from '../core/shell.js';
import { dir, file, GUEST, treeMount } from '../core/vfs.js';

const elfJunk = (): string => {
  const header = '\x7fELF\x02\x01\x01\x00';
  const glyphs = '█░▒▓¶§×ΣЖЯツ乱�';
  const lines = [header];
  for (let i = 0; i < 12; i++) {
    let line = '';
    for (let j = 0; j < 64; j++) {
      const r = Math.random();
      if (r < 0.3) {
        line += String.fromCharCode(0x20 + Math.floor(Math.random() * 95));
      } else if (r < 0.6) {
        line += glyphs[Math.floor(Math.random() * glyphs.length)];
      } else {
        line += String.fromCharCode(Math.floor(Math.random() * 26) + 0x41);
      }
    }
    lines.push(line);
  }
  return lines.join('\n');
};

const buildTmp = () =>
  dir(
    {
      '.secret': file('you were curious enough to check /tmp. respect.'),
      '.pwn': Object.assign(
        file(elfJunk, undefined, { owner: GUEST, group: GUEST, mode: 0o755 }),
        { executable: true }
      ),
    },
    { mode: 0o777 }
  );

const DASH = bold(brightYellow('[-]'));
const STAR = bold(brightCyan('[*]'));
const BANG = bold(brightRed('[!]'));
const PLUS = bold(brightGreen('[+]'));

const FILE_OUT =
  '/bin/login: setuid ELF 64-bit LSB pie executable, x86-64, dynamically linked, interpreter /lib64/ld-linux-x86-64.so.2, BuildID[sha1]=1f3c0d5b2a87f9e4..., for GNU/Linux 3.2.0, stripped';

const CHECKSEC_OUT = [
  `${cyan('[*]')} '/bin/login'`,
  '    Arch:       amd64-64-little',
  `    RELRO:      ${green('Full RELRO')}`,
  `    Stack:      ${green('Canary found')}`,
  `    NX:         ${green('NX enabled')}`,
  `    PIE:        ${green('PIE enabled')}`,
  `    FORTIFY:    ${green('Enabled')}`,
].join('\n');

const EXPLOIT_LINES: Array<{ text: string; delay: number }> = [
  {
    text: `${DASH} Stage 1: leaking remote addresses to bypass ASLR / PIE...`,
    delay: 320,
  },
  {
    text: `${DASH}    abusing %p in the "login: " prompt format string`,
    delay: 220,
  },
  {
    text: `${STAR}    Leaked stack address:    ${bold('0x7ffd94495ca4')}`,
    delay: 200,
  },
  {
    text: `${STAR}    Leaked PIE base:         ${bold('0x55c4a91d2000')}  (login + 0x0)`,
    delay: 200,
  },
  {
    text: `${STAR}    Leaked libc base:        ${bold('0x7f3b2a800000')}  (libc-2.39.so + 0x0)`,
    delay: 220,
  },
  { text: '', delay: 80 },
  {
    text: `${DASH} Stage 2: leaking stack canary via the same disclosure...`,
    delay: 280,
  },
  {
    text: `${STAR}    Leaked stack canary:     ${bold('0x4f2a8c91e3760a00')}`,
    delay: 220,
  },
  {
    text: `${BANG}    canary low byte = 0x00 — string-terminator quirk, expected on glibc`,
    delay: 220,
  },
  { text: '', delay: 80 },
  {
    text: `${DASH} Stage 3: building ROP chain against leaked bases...`,
    delay: 240,
  },
  {
    text: `${STAR}    pop rdi; ret  =  PIE  + 0x1263   -> ${bold('0x55c4a91d3263')}`,
    delay: 120,
  },
  {
    text: `${STAR}    ret           =  PIE  + 0x101a   -> ${bold('0x55c4a91d301a')}`,
    delay: 120,
  },
  {
    text: `${STAR}    "/bin/sh"     =  libc + 0x1d8678 -> ${bold('0x7f3b2a9d8678')}`,
    delay: 120,
  },
  {
    text: `${STAR}    system        =  libc + 0x52290  -> ${bold('0x7f3b2a852290')}`,
    delay: 140,
  },
  {
    text: `${STAR}    payload: ${dim("'A'*136 + p64(canary) + 'B'*8 + p64(ret) + p64(pop_rdi) + p64(bin_sh) + p64(system)")}`,
    delay: 240,
  },
];

const renderAnsiInto = (text: string, parent: ParentNode): void => {
  for (const seg of parseAnsi(text)) {
    const style = styleFor(seg.attrs);
    if (style) {
      const span = document.createElement('span');
      if (style.classes.length) {
        span.className = style.classes.join(' ');
      }
      if (style.style) {
        span.setAttribute('style', style.style);
      }
      span.textContent = seg.text;
      parent.append(span);
    } else {
      parent.append(document.createTextNode(seg.text));
    }
  }
};

const cutscene = async (ctx: Ctx): Promise<void> => {
  ctx.term.appendEntry('file /bin/login', FILE_OUT);
  await ctx.sleep(300);

  ctx.term.appendEntry('checksec --file=/bin/login', CHECKSEC_OUT);
  await ctx.sleep(380);

  const exploitEntry = ctx.term.appendEntry('./login_pwn.py', null);
  const exploitOut = document.createElement('div');
  exploitOut.className = 'out';
  exploitEntry.append(exploitOut);

  for (const line of EXPLOIT_LINES) {
    renderAnsiInto(line.text + '\n', exploitOut);
    await ctx.sleep(line.delay);
  }

  renderAnsiInto(
    `${DASH} Stage 4: delivering 1208-byte payload to login on stdin `,
    exploitOut
  );
  for (let i = 0; i < 24; i++) {
    exploitOut.append(document.createTextNode('.'));
    await ctx.sleep(40);
  }
  renderAnsiInto(' ' + brightGreen('done') + '\n', exploitOut);
  await ctx.sleep(160);
  renderAnsiInto(
    `${PLUS} canary intact, RIP redirected into ROP chain\n`,
    exploitOut
  );
  await ctx.sleep(140);
  renderAnsiInto(
    `${BANG} ${bold('Arbitrary code execution achieved. Pwned!')}\n`,
    exploitOut
  );
  await ctx.sleep(260);
};

const install: PluginInstall = kernel => {
  kernel.registerMount(treeMount('/tmp', buildTmp));

  kernel.installExecutable('/tmp/.pwn', {
    async exec(ctx) {
      await cutscene(ctx);

      kernel.identity.switchTo('root');
      kernel.setCwd('/root');

      await ctx.run('whoami');
      await ctx.run('id');

      return 0;
    },
  });
};

export default install;
